import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../lib/api';

export const askQuestion = createAsyncThunk(
  'chat/ask',
  async (payload, { getState, rejectWithValue }) => {
    const { email, active_thread_id } = getState().chat;

    const question = typeof payload === 'string' ? payload : payload?.question;
    const siteToken = typeof payload === 'string' ? '' : payload?.site_token || '';
    const threadId = typeof payload === 'string'
      ? active_thread_id || ''
      : payload?.thread_id || active_thread_id || '';

    try {
      const res = await api.post(
        '/chat/',
        {
          question,
          email,
          site_token: siteToken,
          widget_token: siteToken,
          thread_id: threadId,
        },
        { suppressToast: true }
      );

      return {
        question,
        answer: res.data.answer || '',
        chat_id: res.data.chat_id,
        handover_id: res.data.handover_id || null,
        handover_status: res.data.handover_status || '',
        agent_name: res.data.agent_name || '',
        thread_id: res.data.thread_id || threadId || '',
        model_used: res.data.model_used || '',
      };
    } catch (e) {
      const status = e?.response?.status;
      const statusText = e?.response?.statusText;
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail;

      if (msg) {
        const normalized = String(msg).toLowerCase();

        if (
          normalized.includes('not configured') ||
          normalized.includes('not configured for this workspace') ||
          normalized.includes('not fully configured')
        ) {
          return rejectWithValue('Chat is not fully configured yet. Please contact support.');
        }

        return rejectWithValue(msg);
      }

      if (typeof e?.response?.data === 'string' && e.response.data.trim()) {
        return rejectWithValue(e.response.data.trim().slice(0, 240));
      }

      if (status) {
        return rejectWithValue(`Chat request failed (${status}${statusText ? ` ${statusText}` : ''}).`);
      }

      return rejectWithValue(e?.message || 'Network Error');
    }
  }
);

export const requestWidgetHandover = createAsyncThunk(
  'chat/requestWidgetHandover',
  async ({ message, email, name, thread_id, site_token }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        '/widget/chat-handover/',
        {
          message,
          email,
          name,
          thread_id,
          site_token,
        },
        { suppressToast: true }
      );

      return res.data;
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.error ||
        (e?.response ? 'Failed to request handover.' : 'Network error: unable to reach handover service.')
      );
    }
  }
);

export const fetchWidgetHandoverStatus = createAsyncThunk(
  'chat/fetchWidgetHandoverStatus',
  async ({ handover_id, thread_id, site_token }, { rejectWithValue }) => {
    try {
      const res = await api.get('/widget/chat-handover/', {
        params: {
          handover_id,
          thread_id,
          site_token,
        },
        suppressToast: true,
      });

      return res.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data?.error || 'Could not check handover status.');
    }
  }
);

const getInitialGuestName = () => {
  const stored = localStorage.getItem('guest_name');

  if (stored) {
    return stored;
  }

  const generated = `Guest-${Math.random().toString(36).substring(2, 8)}`;
  localStorage.setItem('guest_name', generated);
  return generated;
};

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    history: [],
    email: localStorage.getItem('chat_email') || '',
    guest_name: getInitialGuestName(),
    loading: false,
    active_handover_id: null,
    active_handover_status: '',
    active_thread_id: '',
    active_agent_name: '',
    external_message_ids: [],
  },
  reducers: {
    setEmail: (state, action) => {
      state.email = action.payload;
      localStorage.setItem('chat_email', action.payload);
    },
    addBotMessage: (state, action) => {
      const text = String(action.payload || '').trim();

      if (!text) return;

      state.history.push({ sender: 'bot', text });
    },
    addExternalMessage: (state, action) => {
      const id = String(action.payload?.id || '').trim();
      const text = String(action.payload?.text || '').trim();
      const sender = action.payload?.sender || 'bot';

      if (!id || !text) return;
      if (state.external_message_ids.includes(id)) return;

      state.external_message_ids.push(id);

      if (state.external_message_ids.length > 200) {
        state.external_message_ids = state.external_message_ids.slice(-200);
      }

      state.history.push({
        sender,
        text,
        external_id: id,
        role: action.payload?.role || '',
        created_at: action.payload?.created_at || '',
      });
    },
    setActiveHandover: (state, action) => {
      state.active_handover_id = action.payload?.handover_id || null;
      state.active_handover_status = action.payload?.handover_status || '';
      state.active_thread_id = action.payload?.thread_id || state.active_thread_id || '';
      state.active_agent_name = action.payload?.agent_name || state.active_agent_name || '';
    },
    clearActiveHandover: (state) => {
      state.active_handover_id = null;
      state.active_handover_status = '';
      state.active_agent_name = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(askQuestion.pending, (state) => {
        state.loading = true;
      })
      .addCase(askQuestion.fulfilled, (state, action) => {
        state.loading = false;

        state.history.push({
          sender: 'user',
          text: action.payload.question,
        });

        if (action.payload.answer) {
          state.history.push({
            sender: 'bot',
            text: '',
            fullText: action.payload.answer,
            chat_id: action.payload.chat_id,
            model_used: action.payload.model_used,
          });
        }

        if (action.payload.handover_id) {
          state.active_handover_id = action.payload.handover_id;
          state.active_handover_status = action.payload.handover_status || 'pending';
        }

        if (action.payload.agent_name) {
          state.active_agent_name = action.payload.agent_name;
        }

        if (action.payload.thread_id) {
          state.active_thread_id = action.payload.thread_id;
        }
      })
      .addCase(askQuestion.rejected, (state, action) => {
        state.loading = false;

        const errText =
          (typeof action.payload === 'string' && action.payload.trim()) ||
          action.error?.message ||
          'Sorry, something went wrong.';

        state.history.push({
          sender: 'bot',
          text: errText,
        });
      })
      .addCase(requestWidgetHandover.fulfilled, (state, action) => {
        const handover = action.payload?.handover || {};

        if (handover?.id) {
          state.active_handover_id = handover.id;
          state.active_handover_status = handover.status || 'pending';
          state.active_thread_id = handover.thread_id || state.active_thread_id || '';
          state.active_agent_name = handover.picked_by_name || state.active_agent_name || '';
        }
      })
      .addCase(fetchWidgetHandoverStatus.fulfilled, (state, action) => {
        const status = action.payload?.status || '';
        const agentName = action.payload?.agent_name || '';
        const handover = action.payload?.handover || {};

        if (handover?.id) {
          state.active_handover_id = handover.id;
        }

        if (status) {
          state.active_handover_status = status;
        }

        if (agentName) {
          state.active_agent_name = agentName;
        }

        if (handover?.thread_id) {
          state.active_thread_id = handover.thread_id;
        }
      });
  },
});

export const {
  setEmail,
  addBotMessage,
  addExternalMessage,
  setActiveHandover,
  clearActiveHandover,
} = chatSlice.actions;

export default chatSlice.reducer;